import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Oval } from 'react-loader-spinner';

// Mix of playful gerunds + sophisticated thinking verbs + energy/trading flavor.
const PROCESSING_WORDS = [
  // Playful Claude-style
  'Pondering', 'Cogitating', 'Brewing', 'Crunching', 'Synthesizing',
  'Calibrating', 'Distilling', 'Marinating', 'Untangling', 'Conjuring',
  'Wrangling', 'Mulling', 'Tinkering', 'Forging', 'Computing',
  'Optimizing', 'Iterating', 'Whisking', 'Channeling', 'Investigating',
  'Threading', 'Sifting', 'Probing', 'Aggregating', 'Translating',
  'Orchestrating', 'Spelunking', 'Decoding', 'Plotting', 'Assembling',
  'Annotating', 'Curating', 'Percolating', 'Galvanizing', 'Materializing',
  // Sophisticated / complicated
  'Extrapolating', 'Interpolating', 'Triangulating', 'Disambiguating', 'Recalibrating',
  'Hypothesizing', 'Theorizing', 'Postulating', 'Conjecturing', 'Speculating',
  'Deconstructing', 'Crystallizing', 'Bifurcating', 'Disentangling', 'Excogitating',
  'Cerebrating', 'Amalgamating', 'Quantifying', 'Stratifying', 'Ruminating',
  'Deciphering', 'Reverberating', 'Reticulating', 'Demystifying', 'Reconceptualizing',
  // Energy / trading flavor
  'Reconciling', 'Routing', 'Hedging', 'Balancing', 'Harmonizing',
  'Settling', 'Spreading', 'Dispatching', 'Sweeping', 'Provisioning',
];

const pickRandomDifferent = (current) => {
  let next = current;
  while (next === current) {
    next = Math.floor(Math.random() * PROCESSING_WORDS.length);
  }
  return next;
};

const LoadingOverlay = ({ loading, setLoading }) => {
  const theme = useTheme();

  // Initial word picked once, then rotates while loading.
  const [wordIdx, setWordIdx] = useState(() =>
    Math.floor(Math.random() * PROCESSING_WORDS.length)
  );

  useEffect(() => {
    if (!loading) return undefined;
    const id = setInterval(() => {
      setWordIdx((cur) => pickRandomDifferent(cur));
    }, 2400);
    return () => clearInterval(id);
  }, [loading]);

  if (!loading) return null;

  const isDark = theme?.palette?.mode === 'dark';

  // All visual tokens branched by mode so light gets its own treatment.
  // Solid colors are pulled from the app theme (primary blue family) so the
  // overlay feels native to the rest of the UI.
  const c = isDark
    ? {
        backdrop:      'rgba(10, 14, 22, 0.55)',
        blur:          '3px',
        ovalColor:     '#EF7F1A', // altopColor signature accent
        ovalTrack:     'rgba(239, 127, 26, 0.22)',
        groupGlow:     'rgba(239, 127, 26, 0.45)',
        wordColor:     '#FFA85C', // brightened altop for legible text on dark
        wordDots:      '#FFA85C',
        closeColor:        '#E6EAF2',
        closeColorHover:   '#FFFFFF',
        closeBorder:       'rgba(255,255,255,0.22)',
        closeBorderHover:  'rgba(255,255,255,0.40)',
        closeBg:           'rgba(255,255,255,0.06)',
        closeBgHover:      'rgba(255,255,255,0.12)',
      }
    : {
        backdrop:      'rgba(20, 28, 56, 0.28)',
        blur:          '3px',
        ovalColor:     '#0048E5', // primary.main from theme
        ovalTrack:     'rgba(0, 72, 229, 0.18)',
        groupGlow:     'rgba(0, 72, 229, 0.35)',
        wordColor:     '#0048E5',
        wordDots:      '#0048E5',
        closeColor:        '#0E1726',
        closeColorHover:   '#000000',
        closeBorder:       'rgba(14,23,38,0.28)',
        closeBorderHover:  'rgba(14,23,38,0.48)',
        closeBg:           'rgba(255,255,255,0.85)',
        closeBgHover:      '#FFFFFF',
      };

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label="Loading"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1900,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: c.backdrop,
        backdropFilter: `blur(${c.blur}) saturate(120%)`,
        WebkitBackdropFilter: `blur(${c.blur}) saturate(120%)`,
      }}
    >
      <style>{`
        .lo-word {
          display: inline-block;
        }
        .lo-letter {
          display: inline-block;
          opacity: 0;
          transform: translateY(4px);
          animation: lo-letter-in 320ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        @keyframes lo-letter-in {
          to { opacity: 1; transform: translateY(0); }
        }
        .lo-typing-dots {
          font-weight: 800;
          font-size: 1.6em;
          letter-spacing: 0.02em;
          line-height: 1;
        }
        .lo-typing-dots span {
          display: inline-block;
          opacity: 0.4;
          animation: lo-typing-dot 1.6s ease-in-out infinite;
        }
        .lo-typing-dots span:nth-child(2) { animation-delay: 0.16s; }
        .lo-typing-dots span:nth-child(3) { animation-delay: 0.32s; }
        @keyframes lo-typing-dot {
          0%, 60%, 100% { opacity: 0.4; transform: translateY(0); }
          30%           { opacity: 1;   transform: translateY(-2px); }
        }
        .lo-spinner svg circle {
          stroke-width: 4 !important;
        }
      `}</style>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Box
          className="lo-spinner"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            filter: `drop-shadow(0 2px 10px ${c.groupGlow})`,
          }}
        >
          <Oval
            visible
            height="72"
            width="72"
            ariaLabel="loading"
            color={c.ovalColor}
            secondaryColor={c.ovalTrack}
            strokeWidth={4}
            strokeWidthSecondary={4}
          />
        </Box>

        {/* Rotating processing word */}
        <Typography
          aria-live="polite"
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '15px',
            fontWeight: 600,
            letterSpacing: '0.02em',
            color: c.wordColor,
            minWidth: 160,
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'center',
          }}
        >
          <span key={wordIdx} className="lo-word">
            {PROCESSING_WORDS[wordIdx].split('').map((ch, i) => (
              <span
                key={i}
                className="lo-letter"
                style={{ animationDelay: `${i * 28}ms` }}
              >
                {ch}
              </span>
            ))}
          </span>
          <span
            className="lo-typing-dots"
            style={{ marginLeft: '1px', color: c.wordDots }}
          >
            <span>.</span><span>.</span><span>.</span>
          </span>
        </Typography>
      </Box>

      <IconButton
        onClick={() => setLoading(false)}
        aria-label="Dismiss loading overlay"
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 32,
          height: 32,
          padding: 0,
          color: c.closeColor,
          backgroundColor: c.closeBg,
          border: `1px solid ${c.closeBorder}`,
          borderRadius: '4px',
          transition: 'color 160ms ease, border-color 160ms ease, background-color 160ms ease',
          '&:hover': {
            color: c.closeColorHover,
            backgroundColor: c.closeBgHover,
            borderColor: c.closeBorderHover,
          },
          '&:focus-visible': {
            outline: 'none',
            borderColor: c.closeBorderHover,
            boxShadow: `0 0 0 2px ${c.closeBg}`,
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

export default LoadingOverlay;
